import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * True once the element is on screen or within `rootMargin` of it, and stays true (content loaded on demand is never
 * unloaded again). Used by the panel to ask for its secondary blocks only when the reader gets near them, so on a
 * phone the main indicators are fetched and drawn first. Without IntersectionObserver (old browsers, unit tests)
 * everything loads at once, as before.
 */
export function useNearViewport<T extends Element>(rootMargin = '400px'): [(el: T | null) => void, boolean] {
  const supported = typeof IntersectionObserver === 'function';
  const [near, setNear] = useState(!supported);
  const observer = useRef<IntersectionObserver | null>(null);

  const ref = useCallback(
    (el: T | null) => {
      observer.current?.disconnect();
      observer.current = null;
      if (!el || near || !supported) return;
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            setNear(true);
          }
        },
        // Top margin without limit: an element the reader has already scrolled past (a jump to the end of the page)
        // also counts as near; with a symmetric margin it never "intersected" and its content never loaded.
        { rootMargin: `100000px 0px ${rootMargin} 0px` },
      );
      io.observe(el);
      observer.current = io;
    },
    [near, supported, rootMargin],
  );

  useEffect(() => () => observer.current?.disconnect(), []);
  return [ref, near];
}
